import { ActionIcon, Box, Center, Group, Loader, Stack } from '@mantine/core';
import { useDisclosure, useHover } from '@mantine/hooks';
import { Badge, Button, Icon, Text } from '@repo/foundations';
import { isNil, lowerCase, map, size } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import type { ILineage } from '../../../../api';
import { useLineageInfiniteList } from '../../../../api/hooks/lineage/useLineage';
import { getEntityDisplayTitle } from '../../../../lib/utils/entity';
import { FilterOptionType } from '../../../Filter';
import IntegrationLogo from '../../../IntegrationLogo';
import { LineageDirectionEnum } from '../../types';
import { useStyles } from './ImpactAnalysisCard.styles';
import { ImpactAnalysisCardMetadata } from './ImpactAnalysisCardMetadata';
import { impactAnalysisStore } from './store';

interface IImpactedAnalysisCardProps {
	entity: ILineage;
	depth?: number;
	isLast?: boolean;
	isChild?: boolean;
}

const ImpactAnalysisCard = observer(
	({
		entity,
		depth = 1,
		isLast = true,
		isChild = false,
	}: IImpactedAnalysisCardProps) => {
		const [opened, { toggle }] = useDisclosure(false);
		const { hovered, ref: hoveredRef } = useHover();

		const { data, hasNextPage, fetchNextPage, isLoading } =
			useLineageInfiniteList({
				filters: { id: entity.id || '', ...impactAnalysisStore.queryParams },
				options: {
					enabled: false,
					keepPreviousData: true,
				},
			});

		const hasImpactedEntities =
			impactAnalysisStore.filterValues[FilterOptionType.DIRECTION].value ===
			LineageDirectionEnum.DOWNSTREAM
				? size(entity.downstream_entities) > 0
				: size(entity.upstream_entities) > 0;

		const showCard = lowerCase(entity.title).includes(
			lowerCase(impactAnalysisStore.searchTerm)
		);

		const { classes, theme } = useStyles({
			isHovered: hovered,
			isFirst: depth === 1,
			isLast,
			isChild,
			hasImpactedEntities,
			hasNextPage: Boolean(hasNextPage),
			opened,
		});

		if (!showCard && !opened && !hasImpactedEntities) {
			return null;
		}

		const handleToggle = async () => {
			if (isNil(data) || hasNextPage) {
				fetchNextPage();
			}
			toggle();
		};

		const handleFetchMore = async () => {
			if (isNil(data) || hasNextPage) {
				fetchNextPage();
			}
		};

		return (
			<Stack key={entity.id} className={classes.wrapper} spacing={0}>
				{showCard && (
					<Group
						ref={hoveredRef}
						className={classes.cardWrapper}
						spacing="xs"
						p="xs"
						noWrap
					>
						<Box className={classes.chevronWrapper} onClick={handleToggle}>
							{hasImpactedEntities && (
								<ActionIcon size="xs">
									<Icon
										name={opened ? 'chevronDown' : 'chevronRight'}
										color="icon/secondary/default"
									/>
								</ActionIcon>
							)}
						</Box>
						<Group spacing="xs" w="100%" noWrap>
							<IntegrationLogo
								entityType={entity.entity_type}
								integrationId={entity.integration_id}
								integrationType={entity.integration_type}
								width={theme.other.iconSize.lg}
								height={theme.other.iconSize.lg}
							/>
							<Stack className={classes.titleWrapper} spacing={0}>
								<Text size="sm" truncate>
									{getEntityDisplayTitle(entity)}
								</Text>
								<ImpactAnalysisCardMetadata entity={entity} />
							</Stack>
							<Badge>{`Level ${depth}`}</Badge>
						</Group>
					</Group>
				)}
				{opened && hasImpactedEntities && (
					<Stack spacing={0}>
						{isLoading ? (
							<Center p="sm">
								<Loader size="md" />
							</Center>
						) : (
							map(data?.pages, (child, index) => (
								<ImpactAnalysisCard
									key={child.id}
									entity={child}
									depth={depth + 1}
									isChild={true}
									isLast={
										(depth === 1 || isLast) && index === size(data?.pages) - 1
									}
								/>
							))
						)}
						{hasNextPage && (
							<Button
								variant="tertiary"
								size="md"
								onClick={handleFetchMore}
								loading={isLoading}
							>
								Load more
							</Button>
						)}
					</Stack>
				)}
			</Stack>
		);
	}
);

export default ImpactAnalysisCard;
